<template>
    <div :class="containerClasses">
        <NavigationMobileHeader v-if="hasBoxType(menu) && !menuStore.isDesktop"
                                :title="parentsTitle"
                                @clicked="emit('close-menu')" />
        <div v-for="(childMenu, index) in filteredChildren"
             :key="index"
             :class="{
                 'navigation-item-container': isMenuItem(childMenu),
                 'list-item': isListItem(childMenu),
             }"
             @mouseenter="handleOpen(childMenu)"
             @mouseleave="handleClose(childMenu)">
            <NavigationItem v-if="isMenuItem(childMenu)"
                            class="navigation-item"
                            :class="{
                                'navigation-item--active' : isMenuItem(childMenu) && childMenu.isOpen,
                            }"
                            :title="menuItem(childMenu).title"
                            :badge="menuItem(childMenu).badge"
                            :icon="menuItem(childMenu).icon"
                            :showArrow="hasChildren(childMenu)"
                            :arrowDirection="calculateArrowDirection(childMenu)"
                            :hasChildren="hasChildren(childMenu)"
                            :action="menuItem(childMenu)?.action"
                            :url="menuItem(childMenu)?.url"
                            :url_target="menuItem(childMenu)?.url_target || '_self'"
                            @click="handleClick(childMenu)" />
            <DataMenu v-else
                      :menu="childMenu" />
            <Teleport v-if="isTeleportAllowed"
                      :to="teleportLocation(childMenu)"
                      :disabled="menuStore.isDesktop || !hasBoxType(childMenu)">
                <SlideTransition :disabled="isTransitionDisabled(childMenu)"
                                 :childCount="numberOfChildren"
                                 :childHeight="menuStore.menuItemHeight">
                    <NavigationMenu v-if="hasChildren(childMenu) && childMenu.isOpen && isMenuItem(childMenu)"
                                    :menuLevel="menuLevel + 1"
                                    :menu="childMenu"
                                    :parentsTitle="`${parentsTitle} / ${childMenu.title}`"
                                    @reset-sibling="menuStore.resetChildren(childMenu)"
                                    @close-menu="childMenu.isOpen = false" />
                </SlideTransition>
            </Teleport>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import DataMenu from './DataMenu.vue';
    import { DirectionsEnum } from '@/js/enums/directions';
    import SlideTransition from '../../../transitions/SlideTransition.vue';
    import { useMenuStore } from '@/js/store/menu/MenuStore';
    import NavigationItem from '../data-menus/navigation-item/NavigationItem.vue';
    import {
        INavigationItem, INavigationMenu, TMenu,
    } from '../types/menuTypes';
    import NavigationMobileHeader from '../partials/NavigationMobileHeader.vue';
    import { HideOptionEnum, MenuTypeEnum } from '../enums/menuEnums';
    import { NAVIGATION_ID } from '../HtmlIDs';
    import doesElementExist from '../../../helpers/doesElementExist';

    interface INavigationMenuProps {
        menu: TMenu,
        menuLevel?: number,
        parentsTitle?: string,
        isFirstMenuOpenByDefault?: boolean,
    }

    const props = withDefaults(defineProps<INavigationMenuProps>(), {
        menuLevel: 2,
        parentsTitle: '',
        isFirstMenuOpenByDefault: false,
    });

    const emit = defineEmits<{(e: 'close-menu'): void;
                              (e: 'reset-sibling'): void;
    }>();

    const menuStore = useMenuStore();

    const isTeleportAllowed = ref(false);
    const isMouseEnterEventActive = ref(false);
    const numberOfChildren = ref(0);

    const hasChildren = (menu: TMenu): menu is INavigationMenu => 'children' in menu;
    const hasBoxType = (menu: TMenu): menu is INavigationItem => 'child_box_type' in menu;
    const teleportLocation = (menu) => ((menuStore.isDesktop || !menu.child_box_type) ? 'body' : `#${NAVIGATION_ID}`);

    const shouldShowMenuBorder = computed(() => {
        if (hasChildren(props.menu)) {
            return props.menu.children?.some((submenu) => submenu.isOpen);
        }

        return false;
    });

    const filteredChildren = computed(() => {
        if (hasChildren(props.menu)) {
            return props.menu.children.filter((child) => {
                const hideCondition = menuStore.isDesktop ? HideOptionEnum.DESKTOP : HideOptionEnum.MOBILE;

                return child?.hide !== hideCondition;
            });
        }

        return null;
    });

    const containerClasses = computed(() => [
        `level-${props.menuLevel}-menu`,
        shouldShowMenuBorder.value && !hasBoxType(props.menu) ? 'menu-border' : '',
        hasBoxType(props.menu) ? `container-${props.menu.child_box_type}` : '',
    ]);

    const isMenuItem = (menu): menu is INavigationItem => menu.type === MenuTypeEnum.ITEM;
    const isHeading = (menu) => menu.type === MenuTypeEnum.HEADING;
    const isListItem = (menu) => !menuStore.isDesktop && !hasBoxType(menu) && !hasBoxType(props.menu) && hasChildren(menu) && !isHeading(menu);

    const isTransitionDisabled = (menu) => menuStore.isDesktop || !hasChildren(menu) || !isListItem(menu) || !isMenuItem(menu);

    const menuItem = (menu) => {
        if (isMenuItem(menu)) {
            return menu as INavigationItem;
        }

        return menu;
    };

    const calculateArrowDirection = (menu) => {
        if (menuStore.isDesktop) {
            return DirectionsEnum.RIGHT;
        }

        if (menu.children && menu.child_box_type) {
            return DirectionsEnum.RIGHT;
        }

        if (menu.isOpen) {
            return DirectionsEnum.UP;
        }

        return DirectionsEnum.DOWN;
    };

    const calculateNumberOfChildren = (menu) => {
        if (hasChildren(menu)) {
            numberOfChildren.value = menu.children.length;
        } else {
            numberOfChildren.value = 0;
        }
    };

    const handleOpen = (menu) => {
        if (menuStore.isDesktop) {
            const allowOpen = isMenuItem(menu) && hasChildren(menu) && !isMouseEnterEventActive.value;

            if (allowOpen) {
                emit('reset-sibling');
                isMouseEnterEventActive.value = true;
                menu.isOpen = true;
            }
        }
    };

    const handleClose = (menu) => {
        if (menuStore.isDesktop) {
            const allowClose = isMenuItem(menu) && hasChildren(menu);

            if (allowClose) {
                isMouseEnterEventActive.value = false;
                menu.isOpen = false;
            }
        }
    };

    const handleClick = (menu) => {
        const resetChildMenus = (menu) => {
            if (hasChildren(menu) && isMenuItem(menu)) {
                menu.children.forEach((child) => {
                    child.isOpen = false;
                    resetChildMenus(child);
                });
            }
        };

        if (!menuStore.isDesktop) {
            const isCurrentlyOpen = menu.isOpen;
            resetChildMenus(props.menu);
            menu.isOpen = !isCurrentlyOpen;

            calculateNumberOfChildren(menu);
        }
    };

    const openFirstLevel4Menu = (menu) => {
        if (props.menuLevel === 3 && hasChildren(menu) && isMenuItem(menu) && menuStore.isDesktop) {
            menu.children[0].isOpen = true;
        }
    };

    onMounted(() => {
        openFirstLevel4Menu(props.menu);

        isTeleportAllowed.value = doesElementExist(NAVIGATION_ID);
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/partials/z-index" as ZIndex;

$containerTop: calc(#{legacyVariables.$top-menu-height} + #{legacyVariables.$navbar-height});
$containerTop2: calc(#{legacyVariables.$top-menu-height} + #{legacyVariables.$navbar-height});
$containerTop3: calc(#{legacyVariables.$top-menu-height} + #{legacyVariables.$navbar-height});

@function itemBackground($color) {
    @return linear-gradient(to right, $color calc(100% - 1px), transparent 1px);
}

@function lastChildBackground($baseColor, $color) {
    @return linear-gradient(to left, $baseColor 1px, transparent 1px),
        linear-gradient(to top, $baseColor 10px, $color 10px);
}

@function firstChildBackground($baseColor, $color) {
    @return linear-gradient(to left, $baseColor 1px, transparent 1px),
        linear-gradient($baseColor 10px, $color 10px);
}

.container-template {
    display: flex;
    flex-direction: column;
    padding: legacyVariables.$menu-container-gap;
    border-radius: 0 legacyVariables.$menu-border-radius legacyVariables.$menu-border-radius legacyVariables.$menu-border-radius;
    gap: legacyVariables.$menu-container-gap;
}

.container-small {
    @extend .container-template;
    max-width: 350px;
}

.container-medium {
    @extend .container-template;
    min-width: 402px;
}

.container-large {
    @extend .container-template;
    min-width: 500px;
}

.level-2-menu {
    position: absolute;
    top: 32px;
    left: -12px;
    min-width: 220px;
    border-radius: legacyVariables.$menu-border-radius;
    background: legacyVariables.$white;
    box-shadow: -5px 10px 14px -3px rgba(legacyVariables.$name-pill, 0.4);
    color: legacyVariables.$dark-grey-text;

    &::before {
        position: absolute;
        top: -6px;
        left: 13px;
        width: 0;
        height: 0;
        border-right: 7px solid transparent;
        border-bottom: 7px solid legacyVariables.$white;
        border-left: 7px solid transparent;
        content: "";
    }
}

.level-3-menu,
.level-4-menu {
    position: absolute;
    top: 0;
    left: 100%;
    background: legacyVariables.$white;
    box-shadow: 6px 18px 25px -12px rgba(legacyVariables.$name-pill, 0.4);
    color: legacyVariables.$dark-grey-text;
}

.navigation-item-container {
    width: 225px;

    &:last-child {
        height: calc(#{legacyVariables.$menu-item-height} + 10px);

        .navigation-item {
            padding-bottom: 20px;
            border-bottom-left-radius: legacyVariables.$menu-border-radius;
            border-bottom-right-radius: legacyVariables.$menu-border-radius;

            &.navigation-item--active,
            &:hover {
                background: lastChildBackground(legacyVariables.$white, legacyVariables.$pill-color-contrast);
            }
        }
    }

    &:first-child {
        height: calc(#{legacyVariables.$menu-item-height} + 10px);

        .navigation-item {
            padding-top: 20px;
            border-top-left-radius: legacyVariables.$menu-border-radius;
            border-top-right-radius: legacyVariables.$menu-border-radius;

            &.navigation-item--active,
            &:hover {
                background: firstChildBackground(legacyVariables.$white, legacyVariables.$pill-color-contrast);
            }
        }
    }
}

.navigation-item {
    display: flex;
    overflow: hidden;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;

    &--active,
    &:hover {
        background: itemBackground(legacyVariables.$pill-color-contrast);
    }
}

.menu-border {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;

    &::after {
        position: absolute;
        top: 0;
        right: 0;
        width: legacyVariables.$navigation-menu-shift;
        height: 100%;
        background: legacyVariables.$whisper;
        content: "";
        pointer-events: none;
    }
}

.mobile-nav {
    .level-2-menu,
    .level-3-menu {
        position: relative;
        top: unset;
        left: unset;
        overflow: hidden;
        width: 100%;
        border-radius: unset;
        box-shadow: none;

        &::before,
        &::after {
            display: none;
        }
    }

    .level-4-menu {
        box-shadow: none;
    }

    .container-small,
    .container-medium,
    .container-large {
        position: fixed;
        z-index: ZIndex.$nav-modal-z-index;
        top: calc(#{legacyVariables.$top-menu-height} + #{legacyVariables.$navbar-height});
        right: 0;
        bottom: 0;
        left: 0;
        overflow: auto;
        min-width: unset;
        max-width: unset;

        @media (min-width: legacyVariables.$promotional-bar-3-lines-breakpoint) and (max-width: legacyVariables.$promotional-bar-2-lines-breakpoint) {
            top: $containerTop2;
        }

        @media (min-width: legacyVariables.$promotional-bar-2-lines-breakpoint) {
            top: $containerTop3;
        }
    }

    .navigation-item-container {
        position: relative;
        z-index: ZIndex.$nav-z-index;
        width: 100%;
        height: legacyVariables.$menu-item-height;
        padding: 0 24px 0 32px;
        color: legacyVariables.$black-pearl;

        .navigation-item-container {
            padding: 0 0 0 32px;
        }

        .navigation-item {
            padding: 0;
            background: unset !important; // Mobile should never have background change
        }
    }

    .list-item {
        height: unset;
        min-height: legacyVariables.$menu-item-height;
        flex-direction: column;
        justify-content: center;

        .level-3-menu {
            left: 0;
        }

        .navigation-item {
            padding: 0;
        }
    }

    .menu-border {
        border-radius: unset;
    }
}

.dark-theme {
    .level-2-menu,
    .level-3-menu,
    .level-4-menu {
        background: legacyVariables.$dark-theme-bg;
        color: legacyVariables.$white;
    }

    .level-2-menu {
        &::before {
            border-bottom-color: legacyVariables.$dark-theme-bg;
        }
    }

    .menu-border {
        &::after {
            background: rgba(legacyVariables.$dark-theme-border, 0.5);
        }
    }

    .navigation-item-container {
        &:last-child {
            .navigation-item {
                &.navigation-item--active,
                &:hover {
                    background: lastChildBackground(legacyVariables.$dark-theme-bg, legacyVariables.$dark-grey);
                }
            }
        }

        &:first-child {
            .navigation-item {
                &.navigation-item--active,
                &:hover {
                    background: firstChildBackground(legacyVariables.$dark-theme-bg, legacyVariables.$dark-grey);
                }
            }
        }
    }

    .navigation-item {
        &.navigation-item--active,
        &:hover {
            background: itemBackground(legacyVariables.$dark-grey);
        }
    }

    .menu-arrow {
        fill: legacyVariables.$white;
    }

    .mobile-nav {
        .mobile-header {
            fill: legacyVariables.$white;
        }

        .navigation-item {
            color: legacyVariables.$white;
        }
    }
}
</style>
